import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Zap, Search, LineChart, Link2, Bot, ShieldAlert, Sparkles, Rocket } from "lucide-react";
import { Logo } from "@/components/Logo";

const Feature = ({ icon: Icon, title, desc, span = "", testid }) => (
    <div data-testid={testid} className={`border border-border/60 bg-card/40 p-8 hover:border-primary/60 transition-colors ${span}`}>
        <Icon className="w-6 h-6 text-primary mb-6" strokeWidth={1.5} />
        <h3 className="font-display text-xl mb-2 tracking-tight">{title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
    </div>
);

export default function Landing() {
    const [params] = useSearchParams();
    const refCode = params.get("ref");
    const registerPath = refCode ? `/register?ref=${refCode}` : "/register";
    return (
        <div className="min-h-screen bg-background text-foreground overflow-hidden">
            {/* Nav */}
            <header className="sticky top-0 z-50 border-b border-border/60 bg-background/70 backdrop-blur-xl">
                <div className="max-w-7xl mx-auto px-6 h-24 flex items-center justify-between">
                    <Link to="/" data-testid="brand-link" className="flex items-center gap-2 font-display text-xl tracking-tight">
                        <Logo className="h-24 w-auto" />
                    </Link>
                    <nav className="hidden md:flex gap-8 text-sm text-muted-foreground">
                        <a href="#features" className="hover:text-foreground">Features</a>
                        <a href="#solutions" className="hover:text-foreground">Solutions</a>
                        <Link to="/pricing" className="hover:text-foreground">Pricing</Link>
                    </nav>
                    <div className="flex items-center gap-3">
                        <Link to="/login" data-testid="nav-login-btn" className="text-sm text-muted-foreground hover:text-foreground">Sign in</Link>
                        <Link to={registerPath} data-testid="nav-register-btn" className="text-sm bg-primary text-primary-foreground px-4 py-2 font-medium hover:bg-primary/90">
                            Start free
                        </Link>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="grain-bg relative">
                <div className="max-w-7xl mx-auto px-6 pt-24 pb-32 grid grid-cols-12 gap-6">
                    <div className="col-span-12 lg:col-span-8">
                        <div className="overline mb-6">DIGITAL MARKETING · SEO / WEB / SOCIAL / ADS</div>
                        <motion.h1
                            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
                            className="font-display text-5xl md:text-6xl lg:text-7xl font-black tracking-tight leading-[0.95]"
                        >
                            Growth,<br />
                            <span className="text-primary">engineered end-to-end.</span>
                        </motion.h1>
                        <p className="mt-8 max-w-2xl text-lg text-muted-foreground leading-relaxed">
                            InPhase pairs a full-stack SEO/AEO command center with senior strategists in
                            web design, social media, and paid media — so every channel compounds instead of competing.
                            One dashboard, one team, one number that goes up and to the right.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link to={registerPath} data-testid="hero-cta-primary" className="group bg-primary text-primary-foreground px-6 py-3 font-medium flex items-center gap-2 hover:bg-primary/90">
                                Book my strategy call <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                            </Link>
                            <Link to="/login" data-testid="hero-cta-secondary" className="px-6 py-3 border border-border hover:border-primary text-foreground">
                                Sign in
                            </Link>
                        </div>

                        <div className="mt-16 grid grid-cols-3 gap-6 max-w-2xl">
                            {[
                                ["7,240", "keywords tracked / hr"],
                                ["18ms", "avg audit latency"],
                                ["96%", "AI Overview coverage"],
                            ].map(([v, l]) => (
                                <div key={l} className="border-l border-border pl-4">
                                    <div className="font-mono text-2xl text-primary">{v}</div>
                                    <div className="text-xs text-muted-foreground mt-1">{l}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="col-span-12 lg:col-span-4 lg:pt-24">
                        <div className="border border-border bg-card/60 p-6">
                            <div className="overline mb-3">Live signal · demo.co</div>
                            <div className="flex items-baseline gap-2">
                                <span className="font-mono text-4xl text-primary">64.8</span>
                                <span className="text-sm text-success">+4.2</span>
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">Visibility index (30d)</div>
                            <div className="mt-6 h-24 relative overflow-hidden">
                                <svg viewBox="0 0 120 40" className="w-full h-full">
                                    <polyline points="0,30 10,28 20,25 30,26 40,20 50,18 60,15 70,17 80,12 90,10 100,8 110,6 120,5"
                                              fill="none" stroke="hsl(196 100% 61%)" strokeWidth="1.2" />
                                </svg>
                            </div>
                            <div className="mt-6 pt-6 border-t border-border/60 grid grid-cols-2 gap-4 text-xs">
                                <div>
                                    <div className="text-muted-foreground">AI Overviews</div>
                                    <div className="font-mono text-lg mt-1">42</div>
                                </div>
                                <div>
                                    <div className="text-muted-foreground">Toxic links</div>
                                    <div className="font-mono text-lg mt-1 text-destructive">7</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features bento */}
            <section id="features" className="border-t border-border/60">
                <div className="max-w-7xl mx-auto px-6 py-24">
                    <div className="max-w-2xl mb-16">
                        <div className="overline mb-4">The stack</div>
                        <h2 className="font-display text-4xl md:text-5xl tracking-tight">
                            Nine tools. Four channels. One canvas.
                        </h2>
                    </div>
                    <div className="grid grid-cols-12 gap-4">
                        <Feature testid="feat-keywords" icon={Search} title="Keyword tracker" span="col-span-12 md:col-span-6 lg:col-span-4"
                                 desc="Positions, SERP features, AI Overview presence, and share-of-voice across every intent bucket." />
                        <Feature testid="feat-audit" icon={ShieldAlert} title="Technical audit" span="col-span-12 md:col-span-6 lg:col-span-4"
                                 desc="Core Web Vitals, crawl issues, canonical chains — findings ranked by revenue impact." />
                        <Feature testid="feat-aeo" icon={Bot} title="AEO optimizer" span="col-span-12 md:col-span-6 lg:col-span-4"
                                 desc="Rewrite for AI Overviews, ChatGPT Search & Perplexity. Claude Sonnet 4.6 in the loop." />
                        <Feature testid="feat-cannib" icon={Sparkles} title="Cannibalization detector" span="col-span-12 md:col-span-6 lg:col-span-6"
                                 desc="The silent SEO revenue leak, solved. Embedding-based similarity finds pages competing with each other." />
                        <Feature testid="feat-backlinks" icon={Link2} title="Backlinks & toxicity" span="col-span-12 md:col-span-6 lg:col-span-6"
                                 desc="Referring domains, DR, disavow-ready toxic scoring. Ship the file, not the anxiety." />
                        <Feature testid="feat-competitors" icon={LineChart} title="Competitor gap" span="col-span-12 md:col-span-6 lg:col-span-4"
                                 desc="Shared vs. gap keywords, share-of-voice, and content moats you can actually close." />
                        <Feature testid="feat-live-audits" icon={Zap} title="Live DataForSEO audits" span="col-span-12 md:col-span-6 lg:col-span-4"
                                 desc="Real Lighthouse Core Web Vitals + On-Page crawls of your actual domain — zero simulated data, ever." />
                        <Feature testid="feat-reports" icon={Rocket} title="Automated reports" span="col-span-12 md:col-span-6 lg:col-span-4"
                                 desc="One-click executive PDFs, scheduled snapshots, client-ready white-labeling." />
                    </div>
                </div>
            </section>

            {/* Pain-points section */}
            <section id="solutions" className="border-t border-border/60 bg-card/20">
                <div className="max-w-7xl mx-auto px-6 py-24 grid grid-cols-12 gap-8">
                    <div className="col-span-12 lg:col-span-5">
                        <div className="overline mb-4">Built from thousands of marketer complaints</div>
                        <h2 className="font-display text-4xl tracking-tight">The complaints. Solved.</h2>
                        <p className="mt-6 text-muted-foreground">
                            We combed through years of community threads, forum posts, and support tickets so you don't have to.
                            Every top pain point is a first-class feature — not a settings toggle buried behind a paywall.
                        </p>
                    </div>
                    <div className="col-span-12 lg:col-span-7 space-y-3">
                        {[
                            ["Every 'SEO tool' just shows you fake demo data", "→ InPhase pulls 100% real DataForSEO data — audits, rankings, backlinks, competitors. No mock numbers, ever."],
                            ["Keyword cannibalization is impossible to detect", "→ Embedding-similarity cannibalization scanner."],
                            ["Ahrefs/Semrush cost $500+ / month", "→ InPhase starts free, scales linearly."],
                            ["AI Overviews are stealing my clicks", "→ AEO Optimizer + AI Overview presence tracking per keyword."],
                            ["Toxic backlinks are a black box", "→ Automated toxic scoring with one-click disavow export."],
                        ].map(([q, a]) => (
                            <div key={q} className="border border-border/60 p-5 hover:border-primary/50 transition-colors">
                                <div className="text-sm text-muted-foreground">"{q}"</div>
                                <div className="mt-2 text-primary text-sm font-medium">{a}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section id="pricing" className="border-t border-border/60">
                <div className="max-w-4xl mx-auto px-6 py-24 text-left">
                    <div className="overline mb-4">Start now</div>
                    <h2 className="font-display text-5xl tracking-tight">
                        Ready for phase 2 of<br />your organic growth?
                    </h2>
                    <p className="mt-6 text-muted-foreground max-w-xl">
                        Free to start. No credit card. Full suite. Onboarding takes under 90 seconds — then we'll pair you with a strategist if you want the full-service treatment.
                    </p>
                    <div className="mt-10">
                        <Link to={registerPath} data-testid="footer-cta" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-4 font-medium hover:bg-primary/90">
                            Create account <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            <footer className="border-t border-border/60 py-10">
                <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-4 gap-8 items-start">
                    <div>
                        <Logo className="h-20 w-auto mb-3" />
                        <div className="text-xs text-muted-foreground max-w-xs">
                            Phase 2 Labs Marketing · A digital marketing agency for founders who care about compounding returns.
                        </div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                        <div className="overline mb-3">Services</div>
                        <ul className="space-y-1">
                            <li>Search Engine Optimization</li>
                            <li>Web Design & Development</li>
                            <li>Social Media Management</li>
                            <li>Paid Media (Google / Meta)</li>
                        </ul>
                    </div>
                    <div className="text-xs text-muted-foreground">
                        <div className="overline mb-3">Platform</div>
                        <ul className="space-y-1">
                            <li><a href="#features" className="hover:text-foreground">Features</a></li>
                            <li><a href="#solutions" className="hover:text-foreground">Solutions</a></li>
                            <li><Link to="/pricing" className="hover:text-foreground">Pricing</Link></li>
                        </ul>
                    </div>
                    <div className="text-xs text-muted-foreground">
                        <div className="overline mb-3">Contact</div>
                        <ul className="space-y-1">
                            <li><a href="tel:+18552742732" data-testid="footer-phone" className="hover:text-primary">1-855-2PHASE2</a></li>
                            <li><a href="mailto:contact@phase2labs.net" data-testid="footer-email" className="hover:text-primary">contact@phase2labs.net</a></li>
                            <li><a href="https://www.phase2labs.net" target="_blank" rel="noreferrer" data-testid="footer-website" className="hover:text-primary">phase2labs.net</a></li>
                        </ul>
                    </div>
                </div>
                <div className="max-w-7xl mx-auto px-6 mt-8 pt-4 border-t border-border/40 flex justify-between items-center text-xs text-muted-foreground">
                    <div className="font-mono">© {new Date().getFullYear()} Phase 2 Labs Marketing · All rights reserved</div>
                    <div>Built for marketers who ship.</div>
                </div>
            </footer>
        </div>
    );
}
